/**
 * ComparisonGrid — reusable side-by-side outcome cards for demo surfaces.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cva } from "class-variance-authority";
import { AlertTriangle, CheckCircle2, Circle, Info, ShieldCheck } from "lucide-react";
import { GRAY, PRIMARY, SAFE, WARN, withAlpha } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export type ComparisonTone = "safe" | "warn" | "danger" | "info" | "neutral";

export interface ComparisonGridItem {
  readonly id: string;
  readonly title: React.ReactNode;
  readonly body: React.ReactNode;
  readonly accent?: string;
  readonly badge?: React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly icon?: React.ReactNode;
  readonly metric?: React.ReactNode;
  readonly tone?: ComparisonTone;
}

export interface ComparisonGridProps {
  readonly items: readonly ComparisonGridItem[];
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly minColumnWidth?: number;
}

const comparisonCardClass = cva("csk-comparison-card", {
  variants: {
    tone: {
      danger: "csk-comparison-card--danger",
      info: "csk-comparison-card--info",
      neutral: "csk-comparison-card--neutral",
      safe: "csk-comparison-card--safe",
      warn: "csk-comparison-card--warn",
    },
  },
  defaultVariants: { tone: "neutral" },
});

const TONE_ACCENT: Readonly<Record<ComparisonTone, string>> = {
  danger: WARN[700],
  info: PRIMARY[700],
  neutral: GRAY[500],
  safe: SAFE[700],
  warn: "#f59e0b",
};

const TONE_BG: Readonly<Record<ComparisonTone, string>> = {
  danger: WARN[100],
  info: PRIMARY[100],
  neutral: GRAY[100],
  safe: SAFE[100],
  warn: "#fffbeb",
};

function toneIcon(tone: ComparisonTone, accent: string): React.ReactNode {
  const iconProps = { "aria-hidden": true, color: accent, size: 18 };
  if (tone === "safe") return <ShieldCheck {...iconProps} />;
  if (tone === "danger") return <AlertTriangle {...iconProps} />;
  if (tone === "warn") return <AlertTriangle {...iconProps} />;
  if (tone === "info") return <Info {...iconProps} />;
  return <Circle {...iconProps} />;
}

function colorAlpha(color: string, alphaHex: string): string {
  return /^#[0-9a-f]{6}$/i.test(color) ? withAlpha(color, alphaHex) : color;
}

function Badge({
  children,
  accent,
}: {
  readonly children: React.ReactNode;
  readonly accent: string;
}): React.JSX.Element {
  return (
    <span
      style={{
        alignItems: "center",
        background: colorAlpha(accent, "14"),
        border: `1px solid ${colorAlpha(accent, "40")}`,
        borderRadius: 999,
        color: accent,
        display: "inline-flex",
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 8px",
      }}
    >
      {children}
    </span>
  );
}

export const ComparisonGrid = React.memo(function ComparisonGrid({
  items,
  ariaLabel,
  className,
  minColumnWidth = 260,
}: ComparisonGridProps): React.JSX.Element {
  return (
    <div
      role="list"
      aria-label={ariaLabel}
      className={cn("csk-comparison-grid", className)}
      style={{
        display: "grid",
        gap: 14,
        gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}px, 1fr))`,
      }}
    >
      {items.map((item) => {
        const tone = item.tone ?? "neutral";
        const accent = item.accent ?? TONE_ACCENT[tone];
        const icon = item.icon ?? toneIcon(tone, accent);
        return (
          <article
            key={item.id}
            role="listitem"
            className={comparisonCardClass({ tone })}
            style={{
              background: `linear-gradient(180deg, ${TONE_BG[tone]} 0%, white 100%)`,
              border: `1px solid ${colorAlpha(accent, "42")}`,
              borderRadius: 10,
              boxShadow: `0 10px 24px ${colorAlpha(accent, "10")}`,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              padding: "16px 18px",
            }}
          >
            <div style={{ alignItems: "flex-start", display: "flex", gap: 10 }}>
              <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 2 }}>{icon}</span>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <h3
                    style={{
                      color: GRAY[900],
                      fontSize: 14,
                      fontWeight: 800,
                      lineHeight: 1.35,
                      margin: 0,
                    }}
                  >
                    {item.title}
                  </h3>
                  {item.badge !== undefined ? <Badge accent={accent}>{item.badge}</Badge> : null}
                </div>
                {item.metric !== undefined ? (
                  <div
                    style={{
                      color: accent,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: 30,
                      fontWeight: 800,
                      lineHeight: 1.1,
                      marginTop: 10,
                    }}
                  >
                    {item.metric}
                  </div>
                ) : null}
                <div style={{ color: GRAY[700], fontSize: 13, lineHeight: 1.65, marginTop: 10 }}>
                  {item.body}
                </div>
              </div>
            </div>
            {item.footer !== undefined ? (
              <div
                style={{
                  alignItems: "center",
                  background: colorAlpha(accent, "10"),
                  border: `1px solid ${colorAlpha(accent, "25")}`,
                  borderRadius: 8,
                  color: accent,
                  display: "flex",
                  fontSize: 12,
                  fontWeight: 700,
                  gap: 6,
                  marginTop: 14,
                  padding: "8px 10px",
                }}
              >
                <CheckCircle2 aria-hidden="true" size={14} />
                {item.footer}
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
});
